import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { buildCssVars, type ResponsiveProp, responsiveVars } from "@/lib/responsive";
import { cn } from "@/lib/utils";

export interface RowProps extends HTMLAttributes<HTMLDivElement> {
  as?: ElementType;
  /** Goteira entre colunas, em px. */
  gap?: number;
  gapX?: number;
  gapY?: number;
  /** Quantas colunas a linha tem. Padrão 12, como na v1. */
  columns?: number;
  alignItems?: CSSProperties["alignItems"];
  justifyContent?: CSSProperties["justifyContent"];
  flexDirection?: CSSProperties["flexDirection"];
  children?: ReactNode;
}

/**
 * A linha do grid de doze colunas.
 *
 * A goteira não é passada ao `Col` por prop: a linha declara `--ds2-gap-x` em
 * si mesma e o `Col` a **herda** no próprio padding, compensando com margem
 * negativa para sangrar até a borda. É o mecanismo da v1, e é o que faz um
 * `Row` aninhado dentro de um `Col` reiniciar a goteira dos filhos — e um
 * `div` intermediário não quebrar nada.
 */
export const Row = forwardRef<HTMLDivElement, RowProps>(
  (
    {
      as: Tag = "div",
      gap = 0,
      gapX,
      gapY,
      columns,
      alignItems,
      justifyContent,
      flexDirection,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const computed: CSSProperties = {
      alignItems,
      justifyContent,
      flexDirection,
      ...buildCssVars({
        "--ds2-gap-x": `${gapX ?? gap}px`,
        "--ds2-gap-y": `${gapY ?? gap}px`,
        "--ds2-cols": columns,
      }),
    };

    return (
      <Tag
        ref={ref}
        className={cn("ds2-row", className)}
        style={{ ...computed, ...style }}
        {...props}
      >
        {children}
      </Tag>
    );
  },
);
Row.displayName = "Row";

export interface ColProps extends HTMLAttributes<HTMLDivElement> {
  as?: ElementType;
  /** Quantas colunas ocupar. Um número vale para todas as larguras. */
  span?: ResponsiveProp<number>;
  /** Quantas colunas pular à esquerda. */
  offset?: ResponsiveProp<number>;
  /** `min-width: 0`, para o conteúdo poder truncar em vez de estourar. */
  truncate?: boolean;
  children?: ReactNode;
}

/**
 * Uma coluna dentro de um `Row`.
 *
 * Três coisas mudam em relação à v1, de propósito:
 *
 * - `span={0}` é respeitado. A v1 guardava com truthiness (`base && …`), então
 *   zero era silenciosamente ignorado.
 * - `offset` pode voltar a zero num breakpoint maior. A v1 não tinha classe
 *   `offset-0`, então um offset definido em `base` não tinha como ser desfeito.
 * - O número de colunas vem de `--ds2-cols` (padrão 12), em vez de estar fixo
 *   nas 115 classes que a v1 gerava.
 */
export const Col = forwardRef<HTMLDivElement, ColProps>(
  ({ as: Tag = "div", span, offset, truncate, className, style, children, ...props }, ref) => {
    const computed: CSSProperties = {
      ...responsiveVars(span, "col"),
      ...responsiveVars(offset, "off"),
    };

    return (
      <Tag
        ref={ref}
        className={cn("ds2-col", truncate && "ds2-col-truncate", className)}
        style={{ ...computed, ...style }}
        {...props}
      >
        {children}
      </Tag>
    );
  },
);
Col.displayName = "Col";
